import React from 'react';
import classnames, { Argument } from 'classnames';
import { Lookup } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import { Conditional } from '@codametrix/ui-components';
import { MenuButton } from '../menu-button/menu-button';
import { MenuItem } from '../menu-button/menu-item';
import {
  ImprovementProviderSearch,
  ImprovementProviders
} from './improvement-provider';
import { ImprovementCharge } from './improvement-charge';
import { commonEnums } from '@codametrix/ui-common';

const { DocumentStatus } = commonEnums;

export const isOpenImprovement = (
  improvement: AmplifyAPI.DocumentImprovement
): 'NO_PROVIDER' | 'OPEN_REASON' | 'OTHERS' => {
  const requiredImprovement = improvement?.provider_query ?? false;
  const noProviderImprovement =
    requiredImprovement && improvement?.providers?.length === 0;
  const openImprovement =
    requiredImprovement &&
    improvement?.providers?.some(
      (provider) => provider?.provider_query_status === DocumentStatus.OPEN
    );
  return noProviderImprovement
    ? 'NO_PROVIDER'
    : openImprovement
    ? 'OPEN_REASON'
    : 'OTHERS';
};

export type ImprovementMenuProps = {
  showAddProvider: boolean;
  onAddProvider: (ev: React.MouseEvent) => void;
  onDeleteImprovement?: (ev: React.MouseEvent) => void;
};

type ImprovementProps = {
  improvement: AmplifyAPI.DocumentImprovement;
  organization: CMxAPI.Organization;
  processInstance: AmplifyAPI.ProcessInstance;
  providerSearchResults: any[];
  procedureSearchResults: CMxCommonApp.CodeInfo[];
  improvementsAction: CMxCommonApp.Capability;
  activeItem: CMxAPI.CaseRun;
  userContext: CMxAPI.OrganizationContext;
  showUnresolved: boolean;
  onProviderAdd: (improvement: AmplifyAPI.ImprovementRequest) => void;
  onProviderUpdate: (improvement: AmplifyAPI.ImprovementRequest) => void;
  onProviderRemove: (improvement: AmplifyAPI.ImprovementRequest) => void;
  onProviderSearch: (query: CMxCommonApp.LookupQuery) => void;
  onImprovementRemove: (improvement: AmplifyAPI.DocumentImprovement) => void;
  onImprovementUpdate: (improvement: AmplifyAPI.ImprovementRequest) => void;
  showAmplifyFeedback: (feedback: AmplifyAPI.AmplifyFeedback) => void;
  onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => void;
  onSearchClear: (codeQuery: Accelerate.CodeActionContextual) => void;
};

type ImprovementState = {
  showAddProvider: boolean;
};
// ---------------- utilities ----------------- //

/**
 * Formatting for the auto-complete search
 * @param activeIdx
 * @param entity
 * @param idx
 * @returns
 */
const resultFormatter = (activeIdx: number, entity: any, idx: number) => {
  const styleClasses: Argument[] = [
    'search-result',
    'document-improvement-dictionary-result',
    {
      'search-result-submittable': activeIdx === idx
    }
  ];

  return (
    <div
      key={idx}
      data-idx={idx}
      className={classnames(styleClasses)}
      draggable={true}>
      <div className="col-label search-label">
        <div className="right-components">
          <span className="code-chip">
            <span className="cmx-chip">{entity['Error Code']}</span>
          </span>
        </div>
      </div>
      <div className="col-code-description">
        {entity['Documentation Improvement Opportunity Description']}
      </div>
    </div>
  );
};

/**
 * Retrieve item from the search results.  Used to pass to the onAdd handler.
 * @param row
 * @param selectElement
 * @param searchResults
 * @returns
 */
const getSelectionData = (
  row: HTMLDivElement,
  selectElement: HTMLSelectElement,
  searchResults: any
) => {
  const item = row.attributes.getNamedItem('data-idx');
  if (!item) return;
  return searchResults[item.value];
};

const shouldShowResults = (lookupState: CMxCommonApp.CodeLookupState) => {
  const { active, searching, codeQuery } = lookupState;
  if (!active) {
    return false;
  }
  if (searching) {
    return true;
  }
  // one character names are displayable.
  return (codeQuery?.text ?? '').length >= 1;
};

// ---------------- React Components ----------------- //
const ImprovementMenu: React.FC<ImprovementMenuProps> = ({
  showAddProvider,
  onAddProvider,
  onDeleteImprovement
}: ImprovementMenuProps) => {
  const icon = (
    <CMXIcon
      svgIconName="BiomeVerticalMenu"
      svgIconClass="improvement-menu-button"
      svgIconSize="16"
    />
  );
  return (
    <MenuButton icon={icon}>
      {!showAddProvider && (
        <MenuItem onSelect={onAddProvider}>Add provider</MenuItem>
      )}
      <MenuItem onSelect={onDeleteImprovement}>Delete</MenuItem>
    </MenuButton>
  );
};

class Improvement extends React.Component<ImprovementProps, ImprovementState> {
  constructor(props: ImprovementProps) {
    super(props);
    this.state = {
      showAddProvider: props.improvement.providers.length === 0
    };
    this.onImprovementDelete = this.onImprovementDelete.bind(this);
    this.onAddProvider = this.onAddProvider.bind(this);
  }
  componentDidUpdate(prevProps: ImprovementProps): void {
    if (
      prevProps.improvement.providers.length !==
      this.props.improvement.providers.length
    ) {
      this.setState({
        showAddProvider: this.props.improvement.providers.length === 0
      });
    }
  }
  providerQueryIcon = (improvement: AmplifyAPI.DocumentImprovement) =>
    improvement.provider_query ? (
      <CMXIcon
        svgIconName="BiomeVWErrorSVG20"
        svgIconClass="improvement-required"
        svgTitleText="Provider query is required"
      />
    ) : (
      <CMXIcon
        svgIconName="BiomeReports"
        svgIconClass="improvement-not-required"
        svgTitleText="Provider query is not required"
      />
    );

  onImprovementDelete = (ev: React.MouseEvent) => {
    ev.preventDefault();
    this.props.onImprovementRemove(this.props.improvement);
  };

  onAddProvider = (ev: React.MouseEvent) => {
    this.setState({ showAddProvider: !this.state.showAddProvider });
  };

  render() {
    return (
      <Conditional
        some={[
          !this.props.showUnresolved,
          this.props.showUnresolved &&
            isOpenImprovement(this.props.improvement) !== 'OTHERS'
        ]}>
        <div
          className="improvement qa-improvement"
          key={this.props.improvement.document_improvement_guid}>
          <div className="improvement-row">
            <div className="improvement-no-actions">
              <div className="improvement-icon">
                {this.providerQueryIcon(this.props.improvement)}
              </div>
              <div className="improvement-text">
                <span className="improvement-code">
                  <b>{this.props.improvement.code}:</b>{' '}
                </span>
                <span className="improvement-description">
                  {this.props.improvement.description}
                </span>
              </div>
            </div>
            <div className="improvement-actions">
              <Conditional {...this.props.improvementsAction}>
                <ImprovementMenu
                  showAddProvider={this.state.showAddProvider}
                  onAddProvider={this.onAddProvider}
                  onDeleteImprovement={this.onImprovementDelete}
                />
              </Conditional>
            </div>
          </div>
          <div className="improvement-body">
            <div className="provider-rows">
              <ImprovementProviders
                improvement={this.props.improvement}
                improvementsAction={this.props.improvementsAction}
                processInstance={this.props.processInstance}
                onProviderRemove={this.props.onProviderRemove}
                onProviderUpdate={this.props.onProviderUpdate}
              />
            </div>
            <Conditional {...this.props.improvementsAction}>
              {this.state.showAddProvider && (
                <div className="provider-search">
                  <ImprovementProviderSearch
                    improvement={this.props.improvement}
                    improvementsAction={this.props.improvementsAction}
                    organization={this.props.organization}
                    processInstance={this.props.processInstance}
                    providerSearchResults={this.props.providerSearchResults}
                    onProviderAdd={this.props.onProviderAdd}
                    onProviderSearch={this.props.onProviderSearch}
                    showAmplifyFeedback={this.props.showAmplifyFeedback}
                  />
                  <CMXIcon
                    svgIconName="BiomeChevronDownSVG20"
                    svgIconSize="20"
                    svgIconClass="select-checvron"
                  />
                </div>
              )}
            </Conditional>
            <ImprovementCharge
              improvement={this.props.improvement}
              improvementsAction={this.props.improvementsAction}
              processInstance={this.props.processInstance}
              procedureSearchResults={this.props.procedureSearchResults}
              activeItem={this.props.activeItem}
              userContext={this.props.userContext}
              onImprovementUpdate={this.props.onImprovementUpdate}
              onCodeSearch={this.props.onCodeSearch}
              onSearchClear={this.props.onSearchClear}
            />
          </div>
        </div>
      </Conditional>
    );
  }
}
ImprovementMenu.displayName = 'ImprovementMenu';

type ImprovementsState = {
  showUnresolved: boolean;
};

export class Improvements extends React.Component<
  Accelerate.ImprovementsProps,
  ImprovementsState
> {
  static displayName = 'Improvements';
  constructor(props: Accelerate.ImprovementsProps) {
    super(props);
    this.state = {
      showUnresolved: props?.showUnresolved ?? false
    };
    this.handleImprovementAdd = this.handleImprovementAdd.bind(this);
    this.handleImprovementRemove = this.handleImprovementRemove.bind(this);
    this.handleImprovementUpdate = this.handleImprovementUpdate.bind(this);
    this.toggleFilter = this.toggleFilter.bind(this);
    this.handleImprovementAdd = this.handleImprovementAdd.bind(this);
  }

  componentDidUpdate(prevProps: Accelerate.ImprovementsProps) {
    if (prevProps.showUnresolved !== this.props.showUnresolved) {
      this.setState({
        showUnresolved: this.props.showUnresolved
      });
    }
  }
  handleImprovementAdd(
    improvementDictionaryRow: Accelerate.LookupSelectionPayload<Accelerate.ImprovementDictionaryRow>
  ) {
    if (typeof improvementDictionaryRow !== 'undefined') {
      const improvementRequest: AmplifyAPI.ImprovementRequest = {
        processInstance: this.props.processInstance,
        improvementDictionaryRow: improvementDictionaryRow.codeInfo
      };
      this.props.onDocumentImprovementAdd(improvementRequest);
    }
  }
  handleImprovementUpdate(improvement: AmplifyAPI.DocumentImprovement) {
    const improvementRequest: AmplifyAPI.ImprovementRequest = {
      processInstance: this.props.processInstance,
      documentImprovement: improvement
    };
    this.props.onDocumentImprovementUpdate(improvementRequest);
  }
  handleImprovementRemove(documentImprovement: AmplifyAPI.DocumentImprovement) {
    const improvementRequest: AmplifyAPI.ImprovementRequest = {
      processInstance: this.props.processInstance,
      documentImprovement: documentImprovement
    };
    this.props.onDocumentImprovementRemove(improvementRequest);
  }

  toggleFilter(event: React.MouseEvent) {
    event.preventDefault();
    this.setState({
      showUnresolved: !this.state?.showUnresolved
    });
  }

  render() {
    const props: Accelerate.ImprovementsProps = {
      ...this.props
    };
    const { improvements, improvementsAction, organization, processInstance } =
      props;
    const documentImprovements: AmplifyAPI.DocumentImprovement[] =
      improvements?.improvements;
    let displayedImprovements;
    if (documentImprovements.length === 0) {
      displayedImprovements = (
        <div className="empty-message">No Document Improvements.</div>
      );
    } else {
      displayedImprovements = documentImprovements?.map((improvement, idx) => {
        return (
          <Improvement
            key={idx}
            improvement={improvement}
            improvementsAction={this.props.improvementsAction}
            organization={organization}
            processInstance={processInstance}
            providerSearchResults={improvements.providerSearchResults}
            procedureSearchResults={improvements.procedureSearchResults}
            onProviderSearch={this.props.onProviderSearch}
            onImprovementRemove={this.handleImprovementRemove}
            onSearchClear={this.props.onSearchClear}
            onProviderAdd={this.props.onProviderAdd}
            onProviderRemove={this.props.onProviderRemove}
            onProviderUpdate={this.props.onProviderUpdate}
            showAmplifyFeedback={this.props.showAmplifyFeedback}
            onImprovementUpdate={this.props.onDocumentImprovementUpdate}
            onCodeSearch={this.props.onCodeSearch}
            activeItem={this.props.activeItem}
            userContext={improvements.userContext}
            showUnresolved={this.state.showUnresolved}
          />
        );
      });
    }

    const filterLabel = `${
      this.state.showUnresolved ? 'Showing unresolved' : 'Showing all'
    }`;
    return (
      <div className="improvements">
        <Conditional every={[documentImprovements.length > 0]}>
          <div
            className={filterLabel.toLowerCase().replace(' ', '-')}
            onClick={this.toggleFilter}>
            <Conditional every={[this.state.showUnresolved]}>
              <CMXIcon
                svgIconName="BiomeCheckSVG20"
                svgIconClass="unresolved-filter"
                svgIconSize="16"
              />
            </Conditional>
            {filterLabel}
          </div>
        </Conditional>
        {displayedImprovements}
        <Conditional {...improvementsAction}>
          <Lookup
            resultFormatter={resultFormatter}
            getSelectionData={getSelectionData}
            onSearch={this.props.onDocumentImprovementSearch}
            //@ts-ignore
            onSearchSelection={this.handleImprovementAdd as any}
            searchResults={improvements.searchResults}
            placeholderText={'Add reason for document improvement'}
            minLength={1}
            shouldShowResults={shouldShowResults}
            attachResultsToPortal={true}
            resultsPortalExtraClass="improvements-lookup-results-portal"
          />
        </Conditional>
      </div>
    );
  }
}
